import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ecoCardClassName, ecoCardValueClassName } from '../../utils/greenEnergyEcoCard';
import { pickFiniteInsolationPct } from '../../utils/solarInsolationHourly';
import { useSolarInsolationHourly } from '../../hookServices/useSolarInsolationHourly';
import EmsSunIcon from '../EmsSunIcon/EmsSunIcon';
import SolarInsolationDayGraph from '../SolarInsolationDayGraph/SolarInsolationDayGraph';
import '../EmsEcoCardShared.css';
import './EmsSolarEcoCard.css';

const FONT_STACK_PERCENT_SAFE =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';

function formatInsolationPct(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return (
    <>
      {Math.round(Number(value))}
      <span className="ems-solar-eco-card__pct-sign" style={{ fontFamily: FONT_STACK_PERCENT_SAFE }}>
        {' %'}
      </span>
    </>
  );
}

export default function EmsSolarEcoCard({
  insolationTodayPct,
  insolationTomorrowPct,
  pvPowerW,
  latitude = null,
  longitude = null,
  iconRef = null,
  loading = false,
}) {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState('today');
  const {
    todayHourlyInsolation,
    tomorrowHourlyInsolation,
    todayPct: forecastTodayPct,
    tomorrowPct: forecastTomorrowPct,
    loading: forecastLoading,
  } = useSolarInsolationHourly(latitude, longitude);
  const shownTodayPct = pickFiniteInsolationPct(insolationTodayPct, forecastTodayPct);
  const shownTomorrowPct = pickFiniteInsolationPct(insolationTomorrowPct, forecastTomorrowPct);
  const pvKw = pvPowerW != null ? Math.max(0, Number(pvPowerW)) / 1000 : null;
  const pvActive = pvKw != null && pvKw > 0.05;

  return (
    <div
      className={`${ecoCardClassName(pvActive)} ems-solar-eco-card${loading ? ' ems-eco-card--loading' : ''}`}
      aria-busy={loading || undefined}
    >
      <span className="ems-eco-card__title">{t('solar')}</span>
      <div className="ems-solar-eco-card__head">
        <div ref={iconRef} className="ems-solar-eco-card__icon-wrap">
          <EmsSunIcon insolationPct={shownTodayPct} active={pvActive} />
        </div>
        <span className={`${ecoCardValueClassName({ active: pvActive })} ems-solar-eco-card__power`}>
          {pvKw != null ? pvKw.toFixed(2) : '—'}
          {t('powerWt')}
        </span>
      </div>

      <div className="ems-solar-eco-card__days" role="tablist" aria-label={t('solarInsolationForecast')}>
        <button
          type="button"
          role="tab"
          aria-selected={selectedDay === 'today'}
          className={[
            'ems-solar-eco-card__day',
            'ems-solar-eco-card__day--today',
            selectedDay === 'today' ? 'ems-solar-eco-card__day--selected' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setSelectedDay('today')}
        >
          <span className="ems-solar-eco-card__day-label">{t('solarInsolationToday')}</span>
          <span className="ems-solar-eco-card__day-value">{formatInsolationPct(shownTodayPct)}</span>
          {selectedDay === 'today' && <span className="ems-solar-eco-card__day-indicator" aria-hidden />}
        </button>
        <div className="ems-solar-eco-card__day-divider" aria-hidden />
        <button
          type="button"
          role="tab"
          aria-selected={selectedDay === 'tomorrow'}
          className={[
            'ems-solar-eco-card__day',
            'ems-solar-eco-card__day--tomorrow',
            selectedDay === 'tomorrow' ? 'ems-solar-eco-card__day--selected' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setSelectedDay('tomorrow')}
        >
          <span className="ems-solar-eco-card__day-label">{t('solarInsolationTomorrow')}</span>
          <span className="ems-solar-eco-card__day-value ems-solar-eco-card__day-value--tomorrow">
            {formatInsolationPct(shownTomorrowPct)}
          </span>
          {selectedDay === 'tomorrow' && <span className="ems-solar-eco-card__day-indicator" aria-hidden />}
        </button>
      </div>

      <SolarInsolationDayGraph
        latitude={latitude}
        longitude={longitude}
        day={selectedDay}
        todayHourlyInsolation={todayHourlyInsolation}
        tomorrowHourlyInsolation={tomorrowHourlyInsolation}
        loading={forecastLoading}
      />
    </div>
  );
}
